import React, { useEffect, useMemo, useRef, useState } from "react";
import CircularLoading from "../loading/Loading";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  LabelList,
  Tooltip,
  Legend,
  CartesianGrid,
  ReferenceLine,
  Cell,
} from "recharts";
import { Bar as ReactBars } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ReactTooltips,
  Legend as ReactLegend,
} from "chart.js";
import "../components/TdscViewer.css";
import { useNavigate } from "react-router-dom";
import { useParentContext } from "../context/ParentContext";
import { languageCodes } from "../locals/languageCode";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ReactTooltips,
  ReactLegend
);

const TdscChartNew = ({
  pediatricianId,
  patientId,
  currentAge,
  forPdf = false,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const { language } = useParentContext();
  const token = localStorage.getItem("s2stoken");
  const navigate = useNavigate();
  const [translations, setTranslations] = useState(null);
  const [highlightSerials, setHighlightSerials] = useState({
    completed: [],
    missed: [],
  });
  const highlightMonth = currentAge;

  const milestones = useMemo(
    () => [
      { serial: 1, label: "Broad jump (Both legs)", start: 36, end: 37 },
      { serial: 2, label: "Copy circle", start: 36, end: 39 },
      { serial: 3, label: "Balance one foot one second", start: 36, end: 40 },
      { serial: 4, label: "Name one colour", start: 36, end: 45 },
      {
        serial: 5,
        label: "Tells use of 2 objects (Eg: pencil, chair)",
        start: 36,
        end: 45,
      },
      { serial: 6, label: "Concept of one (Pick '1' from a group)", start: 36, end: 46 },
      { serial: 7, label: "Answer 2 questions (Eg: Hungry, cold)", start: 36, end: 49 },
      {
        serial: 8,
        label: "Plays near and talk with peers",
        start: 45,
        end: 50,
      },
      { serial: 9, label: "Hops continuously 3 steps", start: 45, end: 52 },
      { serial: 10, label: "Write 3 alphabets (Eg: A,F,E)", start: 48, end: 56 },
      { serial: 11, label: "Tell function of 3 body parts", start: 49, end: 57 },
      { serial: 12, label: "Paints/shades blank circle", start: 50, end: 59 },
      { serial: 13, label: "Define/explain 10 words", start: 51, end: 59 },
      { serial: 14, label: "Heel to toe walk 4 consecutive steps", start: 48, end: 60 },
      { serial: 15, label: "Answer why questions", start: 51, end: 60 },
      { serial: 16, label: "Folds paper diagonally twice", start: 51, end: 63 },
      { serial: 17, label: "Draw person with 3 parts", start: 48, end: 64 },
      {
        serial: 18,
        label: "Copy 3 shapes",
        start: 53,
        end: 64,
      },
      { serial: 19, label: "Points to middle", start: 52, end: 65 },
      {
        serial: 20,
        label: "Picks 5 objects from the group",
        start: 55,
        end: 66,
      },
      { serial: 21, label: "Button/ unbutton", start: 56, end: 68 },
      { serial: 22, label: "Names days of a week in order", start: 57, end: 70 },
      { serial: 23, label: "Uses 5-6 word sentences", start: 60, end: 72 },
      { serial: 24, label: "Writes own name", start: 61, end: 72 },
    ],
    []
  );

  const getLanguageData = async (selectedLanguage) => {
    try {
      if (selectedLanguage === "english") return null;
      const languageModule = await import(
        `../locals/languages/${selectedLanguage}.json`
      );
      return languageModule.default;
    } catch (error) {
      console.error(`Error loading ${selectedLanguage} translations:`, error);
      return null;
    }
  };
  useEffect(() => {
    const loadTranslations = async () => {
      try {
        const data = await getLanguageData(languageCodes[language]);
        setTranslations(data);
      } catch (error) {
        console.error("Error loading translations:", error);
      }
    };

    loadTranslations();
  }, [language, highlightSerials]);

  const translateMilestone = (milestone) => {
    if (!milestone || !translations) return milestone;
    return translations.milestones?.[milestone] || milestone;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/get_milestone_tracker_doctor_view?pediatrician_id=${pediatricianId}&patient_id=${patientId}`,
          {
            method: "GET",
            credentials: "include",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const data = await response.json();
        if (!response.ok || response.success === false) {
          throw new Error(data.message || "Failed to fetch data");
        }

        const res = data.data;

        console.log("API Response:", res); // Debugging API data

        const completedMilestones = [];
        const missedMilestones = [];

        milestones.forEach((milestone) => {
          const matchingMilestone = res.find(
            (resMil) =>
              resMil["Milestone"]?.trim().toLowerCase() ===
              milestone.label.trim().toLowerCase()
          );

          if (matchingMilestone) {
            if (
              matchingMilestone["Milestone Check"]?.trim().toLowerCase() ===
              "yes"
            ) {
              completedMilestones.push(milestone.serial);
            } else if (milestone.end <= currentAge) {
              missedMilestones.push(milestone.serial);
            }
          }
        });

        console.log("Completed Milestones:", completedMilestones);
        console.log("Missed Milestones:", missedMilestones);

        setHighlightSerials({
          completed: completedMilestones,
          missed: missedMilestones,
        });
      } catch (error) {
        if (error?.message === "Token has expired") {
          localStorage.removeItem("s2stoken");
          navigate("/");
        }
        console.error("Error fetching milestone data:", error);
        alert("Error fetching milestone data.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const tooltipRef = useRef(null);
  const formattedData = useMemo(
    () =>
      [...milestones].map((m) => ({
        milestone: `${m.serial}. ${translateMilestone(m.label)}`,
        start: m.start,
        end: m.end - m.start,
        status: highlightSerials.completed.includes(m.serial)
          ? "Completed"
          : highlightSerials.missed.includes(m.serial)
          ? "Missed"
          : "Pending",
      })),
    [milestones, highlightSerials]
  );

  const CustomTooltip = React.memo(({ active, payload }) => {
    if (!active || !payload || payload.length === 0) return null;
    return (
      <div
        style={{
          background: "#fff",
          padding: "5px",
          border: "1px solid #ccc",
          borderRadius: "6px",
          fontSize: "14px",
          width: "250px",
        }}
      >
        <p>{payload[0].payload.milestone}</p>
        <p>Start: {payload[0].payload.start} Month</p>
        <p>End: {payload[0].payload.end + payload[0].payload.start} Month</p>
      </div>
    );
  });


  const externalTooltipHandler = (context) => {
    const { chart, tooltip } = context;

    let tooltipEl = document.getElementById("chartjs-tooltip");

    // Create tooltip if not already there
    if (!tooltipEl) {
      tooltipEl = document.createElement("div");
      document.body.appendChild(tooltipEl);
    }
    tooltipEl.id = "chartjs-tooltip";
    tooltipEl.style.position = "absolute";
    tooltipEl.style.background = "#fff";
    tooltipEl.style.border = "1px solid #ccc";
    tooltipEl.style.borderRadius = "6px";
    tooltipEl.style.padding = "8px";
    tooltipEl.style.pointerEvents = "none";
    tooltipEl.style.fontSize = "14px";
    tooltipEl.style.width = "200px";
    tooltipEl.style.zIndex = 1000;

    // Hide if not needed
    if (tooltip.opacity === 0) {
      tooltipEl.style.opacity = "0";
      return;
    }

    const dataPoint = tooltip.dataPoints?.[0];
    if (!dataPoint) return;

    const raw = dataPoint.raw;
    const label = dataPoint.label;
    const start = raw[0];
    const end = raw[1];

    tooltipEl.innerHTML = `
    <div>
      <p><strong>${label}</strong></p>
      <p>Start: ${start} Month</p>
      <p>End: ${end} Month</p>
    </div>
  `;

    const canvasRect = chart.canvas.getBoundingClientRect();

    tooltipEl.style.opacity = "1";

    // 🟡 Positioning to LEFT side of the bar
    tooltipEl.style.left = `${
      canvasRect.left +
      window.scrollX +
      tooltip.caretX -
      tooltipEl.offsetWidth -
      50
    }px`;
    tooltipEl.style.top = `${
      canvasRect.top +
      window.scrollY +
      tooltip.caretY -
      tooltipEl.offsetHeight -
      110
    }px`;
  };

  const highlightLinePlugin = {
    id: "highlightLinePlugin",
    afterDraw: (chart) => {
      if (highlightMonth < 36 || highlightMonth > 72) return;
      const {
        ctx,
        scales: { x, y },
      } = chart;
      const xPos = x.getPixelForValue(highlightMonth);

      ctx.save();
      ctx.beginPath();
      ctx.moveTo(xPos, y.top);
      ctx.lineTo(xPos, y.bottom);
      ctx.lineWidth = 2;
      ctx.strokeStyle = "black"; // ✅ Keeping your black highlight line
      ctx.stroke();
      ctx.restore();
    },
  };

  const data = {
    labels: [...milestones].map(
      (m) => `${m.serial}. ${translateMilestone(m.label)}`
    ),
    datasets: [
      {
        label: "Milestones",
        data: milestones.map((m) => [m.start, m.end]),
        backgroundColor: [...milestones].map((m) =>
          highlightSerials.completed.includes(m.serial)
            ? "green" // ✅ Completed milestones
            : highlightSerials.missed.includes(m.serial)
            ? "red" // ❌ Missed milestones
            : "#6464CACC"
        ),
        borderWidth: 1,
        borderColor: "black",
        barPercentage: 0.8,
        categoryPercentage: 1,
      },
    ],
  };

  const options = {
    indexAxis: "y",
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: false,
        external: externalTooltipHandler,
      },
      title: {
        display: true,
        text: "",
        font: {
          size: 18,
          weight: "bold",
        },
        padding: {
          bottom: 0,
        },
      },
    },
    scales: {
      x: {
        min: 36,
        max: 72,
        ticks: { stepSize: 2 },
        title: {
          display: true,
          text: "Age in Months",
          font: {
            size: 18,
            weight: "bold",
          },
        },
      },
      y: {
        reverse: true,
        ticks: { autoSkip: false },
        title: {
          display: true,
          text: "Milestones",
          font: {
            size: 14,
            weight: "bold",
          },
        },
      },
    },
  };

  if (isLoading) {
    return <CircularLoading />;
  }

  return (
    <div className="tdscContainer">
      <div
        style={{
          width: "100%",
          textAlign: "center",
          marginBlock: "16px",
          fontSize: "20px",
        }}
      >
        <p>Trivandrum Development Screening Chart (TDSC) 3-6 Years</p>
        <div className="legend">
          <div>
            <p>Upcomming</p>
            <div className="upcomming"></div>
          </div>
          <div>
            <p>Achived</p>
            <div className="achieved"></div>
          </div>
          <div>
            <p>Missed</p>
            <div className="missed"></div>
          </div>
        </div>
      </div>
      <div
        className={`tdscMobileView ${forPdf ? "notranslate" : ""}`}
        translate={forPdf ? "no" : "yes"}
      >
        <div style={{ width: "100%", height: "600px", overflowX: "auto" }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={formattedData}
              layout="vertical"
              barSize={20}
              barGap={1}
            >
              {/* ✅ Added Grid with vertical lines at each month */}
              <CartesianGrid
                strokeDasharray="0"
                stroke="#C0C0C0"
                vertical={true}
              />

              {/* ✅ X-Axis with all months from 36 to 72 */}
              <XAxis
                type="number"
                domain={[36, 72]} // ✅ Forces full width
                dataKey="start"
                ticks={Array.from({ length: 19 }, (_, i) => 36 + i * 2)}
                interval={0}
                allowDataOverflow={true}
                tick={{ fontSize: 12, textAnchor: "end" }}
                label={{
                  value: "Age in Months",
                  position: "bottom",
                  fontWeight: "bold",
                  dy: 2,
                  dx: -12,
                  fill: "black",
                }}
                tickFormatter={(value) => value.toFixed(0)}
              />
              {/* ✅ Y-Axis with readable milestone names */}
              <YAxis
                reversed={true}
                dataKey="milestone"
                type="category"
                width={10}
                label={{
                  value: "Milestones",
                  angle: -90,
                  position: "insideleft",
                  dx: -4,
                  fontSize: 15,
                  fill: "black",
                  fontWeight: "bold",
                }}
                tick={false}
              />
              <ReferenceLine
                x={currentAge}
                stroke="black"
                strokeWidth={2}
                // strokeDasharray="3"
                label={{
                  value: `Current Age: ${currentAge}`,
                  position: "top",
                  fontSize: 12,
                  dy: -5,
                }}
              />
              <Tooltip
                ref={tooltipRef}
                content={<CustomTooltip />}
                wrapperStyle={{ pointerEvents: "none" }}
                cursor={false}
              />
              <Legend
                layout="horizontal"
                verticalAlign="bottom"
                align="right"
                content={(props) => {
                  const { payload } = props;
                  return (
                    <div style={{ width: "100px", height: "20px" }}>
                      {" "}
                      {/* Adjust size as needed */}
                      {payload.map((entry, index) => (
                        <span
                          key={`item-${index}`}
                          style={{
                            display: "inline-block",
                            width: "10px",
                            height: "10px",
                            backgroundColor: entry.color,
                            margin: "0 5px",
                          }}
                        />
                      ))}
                    </div>
                  );
                }}
              />

              {/* ✅ Bars for milestones */}
              <Bar
                dataKey="start"
                stackId="a"
                fill="lightgray"
                opacity={0.3}
                isAnimationActive={false}
              />
              <Bar dataKey="end" stackId="a" isAnimationActive={false}>
                {formattedData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={
                      highlightSerials.completed.includes(
                        entry.milestone.split(".")[0] * 1
                      )
                        ? "#86fa50"
                        : highlightSerials.missed.includes(
                            entry.milestone.split(".")[0] * 1
                          )
                        ? "#fc5db0"
                        : "#81D4FA"
                    }
                  />
                ))}

                <LabelList
                  dataKey="milestone"
                  content={({ x, y, value, index }) => (
                    <text
                      x={
                        index === 2 ? x + 200 :
                        index === 0 || index === 1 
                          ? x + 120
                          : index === 5
                          ? x + 200
                          : [10, 11, 12, 13, 14, 15, 16].includes(index)
                          ? x
                          : index < 10
                          ? x + 150
                          : x - 10
                      }
                      y={y}
                      dy={10}
                      fill="black"
                      fontSize={10}
                      textAnchor={index > 9 ? "start" : "end"} // Align text properly
                      dominantBaseline="middle"
                    >
                      {value.split(".")[1]}
                    </text>
                  )}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="tdscDesktopView">
        <div
          className="tdscDesktopViewpdf"
          style={{
            width: "100%",
            height: "600px",
            minHeight: "600px",
            // maxHeight: "600px",
          }}
        >
          <ReactBars
            data={data}
            options={{ ...options, maintainAspectRatio: false }}
            plugins={[highlightLinePlugin]}
          />
        </div>
        <div
          id="chartjs-tooltip"
          style={{ position: "absolute", pointerEvents: "none", zIndex: 1000 }}
        />
      </div>
      <p style={{ fontSize: "12px" }}>
        Adapted from Trivandrum Development Screening Chart, courtesy Dr MKC
        Nair.
      </p>
    </div>
  );
};

export default TdscChartNew;
